import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import {
  Button,
  HorizontalFormControl,
  SimpleCheckboxControl,
} from '../../../../common/components';
import { SelectField, TextField } from '../../../../common/components/ReduxFormField';
import { ControlLabel, Form, Row, Col, Modal } from 'react-bootstrap';
import { cancelPolicy, getDetailsForCancellation } from '../../redux/policyActions';
import TextArea from '../../../content/components/TextArea/TextArea';
import { get } from 'lodash';
import { isBroker } from '../../../auth/redux/authActions';
import Icon from '../../../../common/components/Icon/Icon';
import { formatNumber, parseNumber } from '../../../../helpers/formHelpers';
import { numericality, required } from 'redux-form-validators';

const FORM_NAME = 'cancelForm';
const REASON_OPTIONS = [
  {
    label: 'Product unsuitable / misunderstood ',
    value: 'Product unsuitable / misunderstood ',
  },
  { label: 'Product too expensive ', value: 'Product too expensive ' },
  {
    label: 'Alternative product purchased ',
    value: 'Alternative product purchased ',
  },
  {
    label: 'Cover overlapped with pre-existing cover ',
    value: 'Cover overlapped with pre-existing cover ',
  },
  {
    label: 'Product cancelled within cooling off period ',
    value: 'Product cancelled within cooling off period ',
  },
  {
    label: 'Product cancelled by underwriter',
    value: 'Product cancelled by underwriter',
  },
  { label: 'Poor service/complaint ', value: 'Poor service/complaint ' },
  { label: 'Cover no longer required ', value: 'Cover no longer required ' },
  { label: 'Other', value: false },
  { label: 'Not known', value: 'Not known' },
];
const UPDATABLE_PREMIUM_BREAKDOWN_KEYS = ['ex_tax'];

const CancellationBreakdown = ({
  originalPremium,
  initializeAdditionalBreakdown,
  updateTotalRefundAmount,
  isCalculating,
}) => {
  const [show, setShow] = useState(false);
  if (isBroker()) {
    return null;
  }
  const toggleShow = () => {
    if (!show) {
      initializeAdditionalBreakdown();
    }
    setShow(!show);
  };

  const handleBlur = (original) => (e, value) => {
    if (value >= original) updateTotalRefundAmount();
  };

  return (
    <>
      <Row>
        <Col sm={12}>
          <div onClick={toggleShow}>
            <h4>
              Additional Breakdown&nbsp;
              <Icon name={`caret-${show ? 'up' : 'down'}`} />
            </h4>
          </div>
        </Col>
      </Row>

      {show && (
        <>
          <Row style={{ paddingTop: '15px' }}>
            <Col sm={9} smOffset={4}>
              <Field
                label="Admin Fee"
                labelSize={6}
                fieldSize={4}
                name="meta.return_premium_breakdown.ex_tax"
                onBlur={handleBlur(originalPremium.ex_tax)}
                currency
                component={HorizontalFormControl}
                parse={parseNumber}
                format={formatNumber}
                isCalc={isCalculating}
                validate={numericality({
                  '>=': originalPremium.ex_tax,
                  msg: 'Cannot refund more than original value',
                })}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

const CancelButton = ({
  dispatch,
  change,
  reset,
  formValues,
  resource,
  handleSubmit,
  submitting,
  policy,
}) => {
  const [show, setShow] = useState(false);
  const [returnPremium, setReturnPremium] = useState({});
  const [originalPremium, setOriginalPremium] = useState();

  const handleCancelSubmit = (values) => {
    return dispatch(cancelPolicy(values, resource));
  };

  const initializeAdditionalBreakdown = () => {
    const updatableReturnPremium = Object.keys(returnPremium).reduce(
      (carry, premiumKey) => {
        if (UPDATABLE_PREMIUM_BREAKDOWN_KEYS.includes(premiumKey)) {
          carry[premiumKey] = returnPremium[premiumKey];
          return carry;
        }
        return carry;
      },
      {},
    );

    dispatch(change('meta.return_premium_breakdown', updatableReturnPremium));
  };

  const updateTotalRefundAmount = () => {
    return dispatch(
      getDetailsForCancellation(resource.data.id, formValues, (details) => {
        setReturnPremium(details.meta.return_premium.premium);
        if (!originalPremium) {
          setOriginalPremium(details.meta.return_premium.premium);
        }
        const maxRefundAmount = Math.abs(
          get(details, 'meta.return_premium.premium.gross', 0.0),
        ).toFixed(2);
        change('meta.max', maxRefundAmount);
        change('meta.gross_return_premium', maxRefundAmount);
      }),
    );
  };

  const handleOpen = () => {
    dispatch(reset());
    updateTotalRefundAmount();
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };
  const isPaymentOffline = !get(formValues, 'meta.refund', true);

  return (
    <div className="pull-right">
      <Button
        type="button"
        bsStyle="primary"
        label="Cancel"
        rightIcon="ban"
        isLoading={policy.isCheckingCancel}
        handleClick={handleOpen}
      />

      <Modal show={show} title="Cancel Policy" onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form horizontal onSubmit={handleSubmit(handleCancelSubmit)}>
            {show && (
              <>
                <Row>
                  <Col sm={12}>
                    <ControlLabel>
                      Cancellation reason (for internal records)
                    </ControlLabel>
                    <Field
                      name="meta.reason"
                      options={REASON_OPTIONS}
                      component={SelectField}
                      validate={required({ msg: 'Reason for cancellation is required' })}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    {!get(formValues, 'meta.reason') && (
                      <div>
                        <ControlLabel>Other reason not listed</ControlLabel>
                        <Field
                          name="meta.note.content"
                          type="textarea"
                          component={TextField}
                          validate={required({
                            msg: 'Reason for cancellation is required',
                          })}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <ControlLabel>Cancellation Endorsement</ControlLabel>
                    <Field
                      readOnly={isBroker()}
                      name="meta.endorsement.content"
                      component={TextArea}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col sm={1}>
                    <Field
                      name="meta.refund"
                      width={35}
                      options={[{ label: '', value: true }]}
                      component={SimpleCheckboxControl}
                    />
                  </Col>
                  <Col sm={11}>
                    Refund - The refund will be processed using the original payment
                    method. If you require the refund to be processed via a different
                    payment method please un-tick this box and refer to accounts
                  </Col>
                </Row>
                {isPaymentOffline ? (
                  <Row>
                    <Col sm={12}>
                      <ControlLabel>Reason (lost card etc.) </ControlLabel>
                      <Field
                        name="meta.note.non_refund"
                        type="textarea"
                        readOnly={isBroker()}
                        component={TextField}
                        validate={required({ msg: 'Reason for non refund is required' })}
                      />
                    </Col>
                  </Row>
                ) : (
                  <Row style={{ paddingTop: '15px' }}>
                    <Col sm={9} smOffset={4}>
                      <Field
                        name="meta.gross_return_premium"
                        label="Refund amount (inc. IPT)"
                        readOnly={isBroker()}
                        labelSize={6}
                        fieldSize={4}
                        currency
                        component={HorizontalFormControl}
                        isCalc={policy.isCheckingCancel}
                        validate={numericality({
                          '<=': originalPremium ? originalPremium.gross * -1 : 0,
                          msg: 'Cannot refund more than original value',
                        })}
                      />
                    </Col>
                  </Row>
                )}
                <br />
              </>
            )}

            <CancellationBreakdown
              initializeAdditionalBreakdown={initializeAdditionalBreakdown}
              updateTotalRefundAmount={updateTotalRefundAmount}
              originalPremium={originalPremium}
              isCalculating={policy.isCheckingCancel}
            />
          </Form>
          <Modal.Footer>
            <Button
              type="submit"
              bsStyle="primary"
              isLoading={submitting}
              handleClick={handleSubmit(handleCancelSubmit)}
              label="Cancel Policy"
            />
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  );
};

const form = reduxForm({ form: FORM_NAME })(CancelButton);
const selector = formValueSelector(FORM_NAME);
const mapStateToProps = (state, props) => {
  const values = selector(
    state,
    'meta.refund',
    'meta.reason',
    'meta.max',
    'meta.refund_amount',
    'meta.return_premium_breakdown',
  );

  return {
    policy: state.policy,
    formValues: values,
    initialValues: {
      meta: {
        refund: true,
        refund_amount: '0.00',
        max: '0.00',
        endorsement: {
          title: 'Policy Cancelled',
          content:
            'It has hereby been agreed that policy ' +
            get(props.resource, 'data.attributes.policy_number') +
            " has been cancelled in it's entirety.",
        },
      },
    },
  };
};

CancelButton.propTypes = {
  dispatch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  formValues: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
  policy: PropTypes.object.isRequired,
  phoneNumber: PropTypes.string,
  callback: PropTypes.func,
  label: PropTypes.string,
};

export default connect(mapStateToProps)(form);
